<i18n lang="json">
{
  "zh-cn": {
    "title": "卡片统计",
    "distribution_title": "卡片统计",
    "state_title": "卡片状态",
    "card_status_statistics": "卡状态统计",
    "total_cards": ["共","张"],
    "available_count": "张",
    "available": "可用卡",
    "canceling": "销卡中",
    "canceled": "已销卡",
    "other_status": "其他状态卡",
    "add_card_holders":"新增持卡人",
    "create_card":"创建新的卡片"
  },
  "en": {
    "title": "Card Statistics",
    "distribution_title": "Card Statistics",
    "state_title": "Card Status",
    "card_status_statistics": "Card status statistics",
    "total_cards": ["Total","Cards"],
    "available_count": "Cards",
    "available": "Available",
    "canceling": "Canceling",
    "canceled": "Canceled",
    "other_status": "Other Status",
    "add_card_holders":"New card holders",
    "create_card":"Create a new card"
  }
}
</i18n>

<script setup lang="ts">
import type { dashboardCardBinListType, dashboardCardStatusType } from '@/api/modules/index'
import useCardStore from '@/store/modules/card'
import * as echarts from 'echarts'
import { CountTo } from 'vue3-count-to'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  loading: {
    type: Boolean,
    required: true,
    default: true,
  },
  cardCount: {
    type: Number,
    required: true,
    default: 0,
  },
  cardBinList: {
    type: Array as PropType<dashboardCardBinListType[]>,
    required: true,
    default: () => [],
  },
  cardStatus: {
    type: Object as PropType<dashboardCardStatusType>,
    required: true,
    default: () => ({
      normal: 0,
      canceling: 0,
      cancelled: 0,
      other: 0,
    }),
  },
})
const cardStore = useCardStore()

const { t } = useI18n()

const chartRef = ref()
let chart: echarts.ECharts | null = null

function getChartData() {
  return [
    {
      name: t('available'),
      value: props.cardStatus.normal,
      itemStyle: { color: '#55C639' },
    },
    {
      name: t('canceling'),
      value: props.cardStatus.canceling,
      itemStyle: { color: '#35BAE9' },
    },
    {
      name: t('canceled'),
      value: props.cardStatus.cancelled,
      itemStyle: { color: '#FF3010' },
    },
    {
      name: t('other_status'),
      value: props.cardStatus.other,
      itemStyle: { color: '#7F8085' },
    },
  ]
}
function initChart() {
  if (!chartRef.value) {
    return false
  }

  // 获取数据映射以便在legend中访问
  const dataMap = new Map(getChartData().map(item => [item.name, item.value]))
  const option: echarts.EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
    },
    legend: {
      left: '55%',
      top: 'middle',
      orient: 'vertical',
      itemGap: 20,
      itemWidth: 16,
      itemHeight: 16,
      textStyle: {
        color: '#666',
        fontSize: 14,
        rich: {
          title: {
            color: '#333',
            fontWeight: 'bold',
            width: 120, // 固定名称区域宽度
            align: 'left', // 左对齐
          },
          value: {
            color: '#666',
            width: 60, // 固定数值区域宽度
            align: 'right', // 右对齐
          },
        },
      },
      formatter: (name: string) => {
        const value = dataMap.get(name) || 0
        // 使用富文本实现左右布局
        return `{title|${name}}{value|${value} ${t('available_count')}}`
      },
    },
    series: [
      {
        name: t('card_status_statistics'),
        type: 'pie',
        center: ['25%', '50%'],
        radius: ['30%', '50%'],
        data: getChartData(),
        // ...其他保持不变的配置
      },
    ],
  }

  chart = echarts.init(chartRef.value)
  chart.setOption(option, true)
}
onMounted(() => {
  initChart()
  window.addEventListener('resize', () => chart?.resize())
})

onUnmounted(() => {
  chart?.dispose()
  window.removeEventListener('resize', () => chart?.resize())
})

watch(
  () => props.cardStatus,
  () => {
    // chart?.setOption({
    //   series: [{ data: getChartData() }],
    // })
    chart?.resize({
      // 可指定新尺寸（默认使用容器当前尺寸）
      width: 'auto',
      height: 'auto',
    })
  },
  { deep: true },
)
</script>

<template>
  <FaPageMain
    v-loading="props.loading"
    class="m-0 mb-5 min-h-85 p-0 shadow-[0px_0px_16px_0px_rgba(0,0,0,0.03)]"
    main-class="p-4 flex-1 flex flex-col justify-between"
  >
    <p>
      <span class="font-size-5 c-[var(--el-color-primary-dark-6)] font-bold">{{ t('title') }}</span>
      <span class="inline-block px-3 font-size-4 c-gray">
        {{ t('total_cards[0]') }}
        <CountTo
          class="c-[--el-color-primary-dark-3] font-bold"
          :start-val="0"
          :end-val="Number(props.cardCount)"
          :duration="2000"
          :decimals="0"
          separator=""
          prefix=""
        />
        {{ t('total_cards[1]') }}
      </span>
      <RouterLink
        v-slot="{ navigate }"
        class="float-right ml-2"
        :to="{
          name: 'CardManageList',
        }"
        custom
      >
        <el-button
          type="primary"
          @click="() => {
            cardStore.openAddCard = true
            navigate()
          }"
        >
          <template #icon>
            <FaIcon name="i-ep:plus" />
          </template>
          {{ t('create_card') }}
        </el-button>
      </RouterLink>
      <RouterLink
        v-slot="{ navigate }"
        class="float-right"
        :to="{
          name: 'CardManageHolder',
        }"
        custom
      >
        <el-button
          plain
          type="primary"
          @click="() => {
            cardStore.openAddHolder = true
            navigate()
          }"
        >
          <template #icon>
            <FaIcon name="i-ep:plus" />
          </template>
          {{ t('add_card_holders') }}
        </el-button>
      </RouterLink>
    </p>
    <el-row :gutter="20" class="clear-both">
      <el-col :xl="12" :lg="12" :md="24" :sm="24" :xs="24">
        <p class="font-bold">
          {{ t('distribution_title') }}
        </p>
        <div class="mt-2 min-h-60 w-full">
          <el-row :gutter="8">
            <el-col
              v-for="(item, index) in props.cardBinList"
              :key="index"
              class="mb-2"
              :xl="12" :lg="12" :md="24" :sm="24" :xs="24"
            >
              <div class="rounded-3 bg-gray bg-opacity-5 p-4">
                <FaIcon class="h-5 w-10 align-middle" :name="cardStore.findCardScheme(item.cardScheme).active" />
                <span class="ml-1 align-middle font-bold">{{ item.cardbin }}</span>

                <CountTo
                  class="float-right c-[--el-color-primary-dark-3]"
                  :start-val="0"
                  :end-val="Number(props.cardCount)"
                  :duration="2000"
                  :decimals="0"
                  separator=""
                  :suffix="t('available_count')"
                />
              </div>
            </el-col>
          </el-row>
        </div>
      </el-col>
      <el-col :xl="12" :lg="12" :md="24" :sm="24" :xs="24">
        <p class="font-bold">
          {{ t('state_title') }}
        </p>
        <div class="mt-2 min-h-60 w-full rounded-3 bg-gray bg-opacity-5">
          <div ref="chartRef" class="min-h-60 w-full flex" />
        </div>
      </el-col>
    </el-row>
  </FaPageMain>
</template>

<style scoped>
/* 样式 */
</style>
